/**
 * Events Duck
 *
 * Manages event data from Supabase.
 * Handles fetching, creating, updating events and RSVPs.
 */

import { storableError } from '../util/errors';
import * as log from '../util/log';

// ================ Action types ================ //

export const FETCH_EVENTS_REQUEST = 'app/events/FETCH_EVENTS_REQUEST';
export const FETCH_EVENTS_SUCCESS = 'app/events/FETCH_EVENTS_SUCCESS';
export const FETCH_EVENTS_ERROR = 'app/events/FETCH_EVENTS_ERROR';

export const FETCH_EVENT_DETAILS_REQUEST = 'app/events/FETCH_EVENT_DETAILS_REQUEST';
export const FETCH_EVENT_DETAILS_SUCCESS = 'app/events/FETCH_EVENT_DETAILS_SUCCESS';
export const FETCH_EVENT_DETAILS_ERROR = 'app/events/FETCH_EVENT_DETAILS_ERROR';

export const CREATE_EVENT_REQUEST = 'app/events/CREATE_EVENT_REQUEST';
export const CREATE_EVENT_SUCCESS = 'app/events/CREATE_EVENT_SUCCESS';
export const CREATE_EVENT_ERROR = 'app/events/CREATE_EVENT_ERROR';

export const RSVP_EVENT_REQUEST = 'app/events/RSVP_EVENT_REQUEST';
export const RSVP_EVENT_SUCCESS = 'app/events/RSVP_EVENT_SUCCESS';
export const RSVP_EVENT_ERROR = 'app/events/RSVP_EVENT_ERROR';

export const CLEAR_EVENTS = 'app/events/CLEAR_EVENTS';

// ================ Reducer ================ //

export const initialState = {
  events: [],
  eventDetails: {},
  fetchEventsInProgress: false,
  fetchEventsError: null,
  fetchEventDetailsInProgress: false,
  fetchEventDetailsError: null,
  createEventInProgress: false,
  createEventError: null,
  rsvpInProgress: false,
  rsvpError: null,
};

export default function eventsReducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH_EVENTS_REQUEST:
      return {
        ...state,
        fetchEventsInProgress: true,
        fetchEventsError: null,
      };
    case FETCH_EVENTS_SUCCESS:
      return {
        ...state,
        events: payload.events,
        fetchEventsInProgress: false,
      };
    case FETCH_EVENTS_ERROR:
      return {
        ...state,
        fetchEventsInProgress: false,
        fetchEventsError: payload,
      };

    case FETCH_EVENT_DETAILS_REQUEST:
      return {
        ...state,
        fetchEventDetailsInProgress: true,
        fetchEventDetailsError: null,
      };
    case FETCH_EVENT_DETAILS_SUCCESS:
      return {
        ...state,
        eventDetails: {
          ...state.eventDetails,
          [payload.event.id]: payload.event,
        },
        fetchEventDetailsInProgress: false,
      };
    case FETCH_EVENT_DETAILS_ERROR:
      return {
        ...state,
        fetchEventDetailsInProgress: false,
        fetchEventDetailsError: payload,
      };

    case CREATE_EVENT_REQUEST:
      return {
        ...state,
        createEventInProgress: true,
        createEventError: null,
      };
    case CREATE_EVENT_SUCCESS:
      return {
        ...state,
        events: [payload.event, ...state.events],
        createEventInProgress: false,
      };
    case CREATE_EVENT_ERROR:
      return {
        ...state,
        createEventInProgress: false,
        createEventError: payload,
      };

    case RSVP_EVENT_REQUEST:
      return {
        ...state,
        rsvpInProgress: true,
        rsvpError: null,
      };
    case RSVP_EVENT_SUCCESS:
      return {
        ...state,
        rsvpInProgress: false,
      };
    case RSVP_EVENT_ERROR:
      return {
        ...state,
        rsvpInProgress: false,
        rsvpError: payload,
      };

    case CLEAR_EVENTS:
      return initialState;

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const getEvents = state => state.events?.events || [];
export const getEventDetails = (state, eventId) => state.events?.eventDetails[eventId] || null;
export const isEventActionInProgress = state =>
  state.events?.fetchEventsInProgress ||
  state.events?.createEventInProgress ||
  state.events?.rsvpInProgress;

// ================ Action creators ================ //

const fetchEventsRequest = () => ({ type: FETCH_EVENTS_REQUEST });
const fetchEventsSuccess = events => ({ type: FETCH_EVENTS_SUCCESS, payload: { events } });
const fetchEventsError = error => ({ type: FETCH_EVENTS_ERROR, payload: error, error: true });

const fetchEventDetailsRequest = () => ({ type: FETCH_EVENT_DETAILS_REQUEST });
const fetchEventDetailsSuccess = event => ({
  type: FETCH_EVENT_DETAILS_SUCCESS,
  payload: { event },
});
const fetchEventDetailsError = error => ({
  type: FETCH_EVENT_DETAILS_ERROR,
  payload: error,
  error: true,
});

const createEventRequest = () => ({ type: CREATE_EVENT_REQUEST });
const createEventSuccess = event => ({ type: CREATE_EVENT_SUCCESS, payload: { event } });
const createEventError = error => ({ type: CREATE_EVENT_ERROR, payload: error, error: true });

const rsvpEventRequest = () => ({ type: RSVP_EVENT_REQUEST });
const rsvpEventSuccess = () => ({ type: RSVP_EVENT_SUCCESS });
const rsvpEventError = error => ({ type: RSVP_EVENT_ERROR, payload: error, error: true });

export const clearEvents = () => ({ type: CLEAR_EVENTS });

// ================ Thunks ================ //

/**
 * Fetch events with optional filters
 */
export const fetchEvents = (filters = {}) => dispatch => {
  dispatch(fetchEventsRequest());

  const params = new URLSearchParams(filters);

  return fetch(`/api/supabase/events?${params}`, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  })
    .then(res => {
      if (!res.ok) {
        return res.json().then(data => {
          throw new Error(data.message || 'Failed to fetch events');
        });
      }
      return res.json();
    })
    .then(data => {
      dispatch(fetchEventsSuccess(data.events || []));
      return data.events;
    })
    .catch(e => {
      log.error(e, 'fetch-events-failed');
      dispatch(fetchEventsError(storableError(e)));
      throw e;
    });
};

/**
 * Fetch details for a specific event
 */
export const fetchEventDetails = eventId => dispatch => {
  dispatch(fetchEventDetailsRequest());

  return fetch(`/api/supabase/events/${eventId}`, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  })
    .then(res => {
      if (!res.ok) {
        return res.json().then(data => {
          throw new Error(data.message || 'Failed to fetch event details');
        });
      }
      return res.json();
    })
    .then(data => {
      dispatch(fetchEventDetailsSuccess(data.event));
      return data.event;
    })
    .catch(e => {
      log.error(e, 'fetch-event-details-failed');
      dispatch(fetchEventDetailsError(storableError(e)));
      throw e;
    });
};

/**
 * Create a new event
 */
export const createEvent = eventData => (dispatch, getState) => {
  const { currentUser } = getState().user;

  if (!currentUser) {
    return Promise.reject(new Error('No authenticated user'));
  }

  dispatch(createEventRequest());

  return fetch('/api/supabase/events', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(eventData),
  })
    .then(res => {
      if (!res.ok) {
        return res.json().then(data => {
          throw new Error(data.message || 'Failed to create event');
        });
      }
      return res.json();
    })
    .then(data => {
      dispatch(createEventSuccess(data.event));
      return data.event;
    })
    .catch(e => {
      log.error(e, 'create-event-failed');
      dispatch(createEventError(storableError(e)));
      throw e;
    });
};

/**
 * RSVP to an event
 */
export const rsvpToEvent = (eventId, status = 'attending') => (dispatch, getState) => {
  const { currentUser } = getState().user;

  if (!currentUser) {
    return Promise.reject(new Error('No authenticated user'));
  }

  dispatch(rsvpEventRequest());

  return fetch(`/api/supabase/events/${eventId}/rsvp`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  })
    .then(res => {
      if (!res.ok) {
        return res.json().then(data => {
          throw new Error(data.message || 'Failed to RSVP');
        });
      }
      return res.json();
    })
    .then(() => {
      dispatch(rsvpEventSuccess());
      // Refresh event details to get updated RSVP count
      return dispatch(fetchEventDetails(eventId));
    })
    .catch(e => {
      log.error(e, 'rsvp-event-failed');
      dispatch(rsvpEventError(storableError(e)));
      throw e;
    });
};
